import React from 'react';
import { motion } from 'framer-motion';
import { Droplets, Waves, Star, ChevronRight, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const PHARMACY_BG = 'https://images.unsplash.com/photo-1760307837671-63ee641f9f1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxwaGFybWFjeSUyMGludGVyaW9yfGVufDB8fHx8MTc3MjI3ODgwOHww&ixlib=rb-4.1.0&q=85';

const IntroScreen = ({ onStart }) => {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'hsl(var(--background))' }}>
      {/* Hero Section */}
      <div className="relative flex-1 flex flex-col overflow-hidden" style={{ minHeight: '60vh' }}>
        {/* Background image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${PHARMACY_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        />
        {/* Dark overlay for contrast */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(160deg, hsl(201 82% 22% / 0.88) 0%, hsl(201 82% 30% / 0.75) 50%, hsl(174 55% 25% / 0.82) 100%)' }}
        />

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0" style={{ height: 60, overflow: 'hidden' }}>
          <svg viewBox="0 0 1200 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
            <path d="M0 60 L0 30 Q150 0 300 20 Q450 40 600 15 Q750 -10 900 20 Q1050 50 1200 30 L1200 60 Z" fill="hsl(210, 30%, 97%)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center flex-1 px-6 py-16 text-center">
          {/* Brand badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold tracking-widest uppercase"
              style={{ background: 'hsl(0 0% 100% / 0.15)', color: 'hsl(0 0% 100%)', border: '1px solid hsl(0 0% 100% / 0.3)', backdropFilter: 'blur(8px)' }}>
              <Droplets size={14} />
              <span>Синомарин · Аптеки</span>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif font-semibold leading-tight mb-4"
            style={{ color: 'hsl(0 0% 100%)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', maxWidth: 700 }}
          >
            Симуляция
            <span className="block" style={{ color: 'hsl(195 100% 80%)' }}>аптечной консультации</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base leading-relaxed mb-8"
            style={{ color: 'hsl(195 100% 90%)', maxWidth: 480 }}
          >
            Интерактивный тренинг для провизоров по правильной рекомендации линейки Синомарин
          </motion.p>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center gap-6 mb-10"
          >
            {[
              { value: '4', label: 'сцены' },
              { value: '12', label: 'вариантов' },
              { value: '5 мин', label: 'прохождение' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl font-semibold" style={{ color: 'hsl(0 0% 100%)' }}>{stat.value}</div>
                <div className="text-xs" style={{ color: 'hsl(195 100% 85%)' }}>{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <button
              onClick={onStart}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-semibold"
              style={{
                background: 'hsl(0 0% 100%)',
                color: 'hsl(201 82% 35%)',
                boxShadow: '0 8px 30px hsl(0 0% 0% / 0.25)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 40px hsl(0 0% 0% / 0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 30px hsl(0 0% 0% / 0.25)'; }}
            >
              <Waves size={20} />
              Начать тренинг
              <ChevronRight size={18} />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Feature cards row */}
      <div className="px-6 py-10" style={{ background: 'hsl(var(--background))' }}>
        <div className="max-w-3xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ color: 'hsl(var(--muted-foreground))' }}
          >
            Чему вы научитесь
          </motion.p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: '💬',
                title: 'Правильный диалог',
                desc: 'Задавайте нужные вопросы в верном порядке'
              },
              {
                icon: '🎯',
                title: 'Подбор продукта',
                desc: 'Точная рекомендация по возрасту и симптомам'
              },
              {
                icon: '📈',
                title: 'Увеличение чека',
                desc: 'Аргументируйте дополнительную ценность'
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className="rounded-2xl p-5 flex flex-col gap-2"
                style={{
                  background: 'hsl(var(--card))',
                  border: '1.5px solid hsl(var(--border))',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <span className="text-2xl">{item.icon}</span>
                <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{item.title}</p>
                <p className="text-xs leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroScreen;
